import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Section, useAuth, useConfirm, useToast } from "@ozpanel/ui";
import { RowInput } from "./Form";
import { StatusPill, type StatusTone } from "./PageHeader";

// DatabaseTransfer — load an SQL dump into one of an account's own databases, or dump one into its home
// (databases/transfer.go). One component for the customer's console and the operator's; the server
// decides who may touch which account. Both are jobs: the page follows /ozpanel/jobs/{id} until it ends.

type JobStep = { name: string; status: string; log?: string; error?: string };
type Job = { id: string; type: string; status: string; progress: number; current_step?: string; error?: string; steps?: JobStep[] | null };
type Started = { job_id: string; database: string; file: string; note?: string };

const tone: Record<string, StatusTone> = { done: "ok", failed: "bad", canceled: "warn", running: "warn", pending: "neutral" };

export function DatabaseTransfer({ tenantId, databases }: { tenantId: string | number; databases: { name: string; engine: string }[] }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [db, setDb] = React.useState("");
  const [file, setFile] = React.useState("");
  const [started, setStarted] = React.useState<Started | null>(null);
  const sel = databases.find((d) => d.name === db) ?? databases[0];

  const jobQ = useQuery({
    queryKey: ["db-transfer-job", started?.job_id],
    queryFn: () => client.apiFetch<Job>(`/ozpanel/jobs/${encodeURIComponent(started!.job_id)}`),
    enabled: !!started,
    refetchInterval: (q) => {
      const s = (q.state.data as Job | undefined)?.status;
      return s === "done" || s === "failed" || s === "canceled" ? false : 1500;
    },
  });

  const confirm = useConfirm();
  const start = async (what: "import" | "export") => {
    if (!sel) return;
    try {
      const name = encodeURIComponent(sel.name);
      const r =
        what === "import"
          ? await client.apiFetch<Started>(`/tenants/${tenantId}/databases/${name}/import`, {
              method: "POST",
              body: JSON.stringify({ file: file.trim() }),
            })
          : await client.apiFetch<Started>(`/tenants/${tenantId}/databases/${name}/export`, { method: "POST" });
      setStarted(r);
    } catch (e) {
      notify({ kind: "error", title: `The ${what} did not start`, body: e instanceof Error ? e.message : String(e) });
    }
  };

  const j = jobQ.data;
  const running = !!j && (j.status === "running" || j.status === "pending");
  return (
    <Section title="Import & export" hint="SQL dumps, as jobs">
      {confirm.dialog}
      <div className="space-y-3 p-4 text-[13px]">
        {databases.length === 0 ? (
          <p className="text-muted">Create a database first.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted">Database</span>
              <select
                value={sel?.name ?? ""}
                onChange={(e) => setDb(e.target.value)}
                className="rounded-lg border border-line2 bg-bg px-2 py-1 font-mono text-[12px] text-fg"
              >
                {databases.map((d) => (
                  <option key={d.name} value={d.name}>{d.name} ({d.engine})</option>
                ))}
              </select>
              <Button size="sm" disabled={running} onClick={() => void start("export")}>Export to my home</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <RowInput value={file} placeholder="db-exports/shop.sql.gz" onChange={setFile} />
              <Button
                size="sm"
                variant="danger"
                disabled={running || !file.trim()}
                onClick={() =>
                  confirm.ask(
                    {
                      title: `Import into ${sel?.name ?? "this database"}?`,
                      body: "The dump's statements run against this database and change what is in it. A failure part-way can leave it partly loaded.",
                      confirmLabel: "Import",
                    },
                    () => start("import"),
                  )
                }
              >
                Import into {sel?.name}
              </Button>
            </div>
            <p className="text-[11.5px] leading-relaxed text-muted">
              Upload a <span className="font-mono">.sql</span> or <span className="font-mono">.sql.gz</span> dump with
              the file manager or SFTP, then name it here (a path in your home). It is loaded as the database's own
              account — never with more rights than the database has — and a dump that uses or creates another
              database is refused before anything runs. Export writes{" "}
              <span className="font-mono">db-exports/&lt;database&gt;-&lt;time&gt;.sql.gz</span> to your home. MariaDB
              imports apply statement by statement, so a failure part-way leaves the statements before it;
              PostgreSQL imports are one transaction. Make PostgreSQL dumps with{" "}
              <span className="font-mono">pg_dump --no-owner</span>.
            </p>
          </>
        )}
        {started && (
          <div className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[12px]">{started.database}</span>
              <span className="text-muted">{started.file}</span>
              {j && <StatusPill tone={tone[j.status] ?? "neutral"}>{j.status}{running ? ` · ${j.progress}%` : ""}</StatusPill>}
            </div>
            {j?.error && <div className="mt-2 break-words text-[12px] text-bad">{j.error}</div>}
            {(j?.steps ?? []).filter((s) => s.log).map((s) => (
              <div key={s.name} className="mt-1 break-words font-mono text-[11.5px] text-muted">{s.name}: {s.log}</div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
